/**
 * The hub's card registry — interface I-3 (C11, FR-A8 §1–§2).
 *
 * **Every settings page has one entry here, and the hub draws only from
 * this list.** 12A wrote the cards for all nine pages, including the two it
 * does not build — *Profession & website conduct* (12B) — and the one whose
 * data is 12E's (*Client sign-in*, whose line reads interface I-4). A later
 * session adding a page appends one entry at the end of `SETTINGS_CARDS`
 * (waves.md §1.2: one block, appended, never a reorder).
 *
 * A card's `summary` is *what is set now* — one line, from the service's
 * `GET /firm/settings-summary` for the cards whose data is the firm module's,
 * and from the page's own API where it is not.
 *
 * **Wave 14** (C13, ADR-0032): *Client sign-in* and *Domain* moved to *Your
 * website* with *Social profiles & map* and the conduct switches; the hub
 * links there in one row (`MOVED_TO_WEBSITE`), and their old addresses
 * redirect.
 */

import type { LucideIcon } from 'lucide-react'
import {
  Building2,
  GitBranch,
  ListChecks,
  Plug,
  Receipt,
  Scale,
  ShieldCheck,
} from 'lucide-react'

import type { SettingsSummary, SummaryLine } from '../../../lib/api/firm'

export type SettingsGroup = 'practice' | 'money' | 'clients' | 'setup'

export const SETTINGS_GROUPS: Array<{ key: SettingsGroup; title: string; description: string }> = [
  {
    key: 'practice',
    title: 'Your practice',
    description: 'Who the firm is, what it practises, and where its offices are.',
  },
  {
    key: 'money',
    title: 'Billing',
    description: 'How invoices are numbered and taxed, and who approves money.',
  },
  {
    key: 'clients',
    title: 'Connected services',
    description: 'What the platform does for you.',
  },
  { key: 'setup', title: 'Getting started', description: 'The setup list on your Overview.' },
]

/** What a card's line may read from. `null` while it loads or if its read
 *  failed — a card then shows its description alone, never a stale line. */
export interface HubData {
  summary: SettingsSummary | null
}

export interface SettingsCard {
  key: string
  title: string
  /** What the page is for — shown under the title. */
  description: string
  href: string
  icon: LucideIcon
  group: SettingsGroup
  summary: (data: HubData) => SummaryLine | null
  /** Absent means always shown. */
  visible?: (data: HubData) => boolean
}

const fromSummary =
  (key: string) =>
  ({ summary }: HubData): SummaryLine | null =>
    summary?.cards[key] ?? null

export const SETTINGS_CARDS: SettingsCard[] = [
  {
    key: 'firm',
    title: 'Your firm',
    description: 'Names, registrations, address, contact and working hours.',
    href: '/settings/firm',
    icon: Building2,
    group: 'practice',
    summary: fromSummary('firm'),
  },
  {
    key: 'profession',
    title: 'Profession',
    description: 'What your practice is. It sets your website’s conduct defaults.',
    href: '/settings/profession',
    icon: Scale,
    group: 'practice',
    summary: fromSummary('profession'),
  },
  {
    key: 'branches',
    title: 'Branches',
    description: 'Your offices. Clients and cases carry one; nothing is hidden by it.',
    href: '/settings/branches',
    icon: GitBranch,
    group: 'practice',
    summary: fromSummary('branches'),
  },
  {
    key: 'billing',
    title: 'Billing defaults',
    description: 'Invoice series, financial year, place of supply, review and rounding.',
    href: '/settings/billing',
    icon: Receipt,
    group: 'money',
    summary: fromSummary('billing'),
  },
  {
    key: 'approvals',
    title: 'Approvals',
    description: 'Who may issue, credit and refund.',
    href: '/settings/approvals',
    icon: ShieldCheck,
    group: 'money',
    summary: fromSummary('approvals'),
  },
  {
    key: 'integrations',
    title: 'Integrations',
    description: 'Payments and messaging your organisation has switched on.',
    href: '/settings/integrations',
    icon: Plug,
    group: 'clients',
    summary: fromSummary('integrations'),
  },
  {
    key: 'checklist',
    title: 'Setup checklist',
    description: 'Rows you marked done or skipped on your Overview.',
    href: '/settings/checklist',
    icon: ListChecks,
    group: 'setup',
    summary: fromSummary('checklist'),
    // FR-A8 §2: only when marks exist, as the old page did.
    visible: ({ summary }) => Boolean(summary?.checklist_visible),
  },
]

/** The cards to draw, by group, in registry order — empty groups dropped. */
export function visibleCards(data: HubData) {
  return SETTINGS_GROUPS.map((group) => ({
    group,
    cards: SETTINGS_CARDS.filter(
      (card) => card.group === group.key && (card.visible ? card.visible(data) : true),
    ),
  })).filter((entry) => entry.cards.length > 0)
}

/**
 * Where an old `/settings#…` link lands (T-1.20: *"old links still land"*).
 * The single page had no anchors of its own, but other screens and notes said
 * *"Settings → Branches"*, and a hash is the natural way such a link was
 * written. Unknown hashes stay on the hub.
 */
export const LEGACY_HASHES: Record<string, string> = {
  firm: '/settings/firm',
  profile: '/settings/firm',
  'your-firm': '/settings/firm',
  billing: '/settings/billing',
  behaviour: '/settings/billing',
  approvals: '/settings/approvals',
  money: '/settings/approvals',
  branches: '/settings/branches',
  profession: '/settings/profession',
  integrations: '/settings/integrations',
  checklist: '/settings/checklist',
  // w14a — moved under the website (C13, ADR-0032 §1): the conduct switches,
  // the domain, client sign-in and the social profiles live there now.
  policy: '/site/policy',
  conduct: '/site/policy',
  domain: '/site/domain',
  'sign-in': '/site/client-sign-in',
  'client-sign-in': '/site/client-sign-in',
  social: '/site/social',
  website: '/site',
}

/**
 * **What moved to *Your website*** (C13, ADR-0032 §1–§2). The Settings hub
 * lost these cards in Wave 14 and draws one row pointing at the website hub
 * instead, naming them so a firm that learnt them here finds them.
 */
export const MOVED_TO_WEBSITE = ['Client sign-in', 'Domain', 'Social profiles & map', 'What you may say']

/**
 * **The old addresses of the pages that moved** (ADR-0032 §4: *"moved pages
 * keep working at their old addresses"*). `router.tsx` answers each `from`
 * with a redirect to `to`; `/settings/firm#social` is the one a route cannot
 * match (a hash is not part of the path) and is sent on by the route itself.
 */
export const MOVED_ADDRESSES: ReadonlyArray<{ from: string; to: string }> = [
  { from: '/settings/client-sign-in', to: '/site/client-sign-in' },
  { from: '/settings/domain', to: '/site/domain' },
]

/** Where `/settings/firm#social` goes now. */
export const MOVED_SOCIAL = '/site/social'

export function legacyTarget(hash: string): string | null {
  const key = hash.replace(/^#/, '').toLowerCase()
  return key ? (LEGACY_HASHES[key] ?? null) : null
}
